import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";
import type { LegacyApiResponse } from "../types/api";
import type { StepUpHandling } from "../services/stepUp";
import { fetchEnvelope, fetcher } from "./fetcher";

type QueryKeyPart = string | number | boolean | null | undefined;
type QueryKey = QueryKeyPart[];
const DEFAULT_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

type ApiQueryProps<TResponse, TBody = unknown> = {
  key: QueryKeyPart[];
  endPoint: string;
  method?: string;
  bodyContent?: TBody;
  body?: TBody;
  enabled?: boolean;
  dataIndex?: string;
  queryOptions?: Omit<
    UseQueryOptions<TResponse, Error, TResponse, QueryKey>,
    "queryKey" | "queryFn"
  >;
  dynamicKey?: QueryKeyPart;
};

type ApiMutationArgs<TBody = unknown> = {
  endPoint: string;
  method?: string;
  bodyContent?: TBody;
  body?: TBody;
  key?: QueryKeyPart[];
  dynamicKey?: QueryKeyPart;
  dataIndex?: string;
  enabled?: boolean;
  /** Extra request headers, e.g. the admin step-up { "X-2FA-Code": code }. */
  headers?: Record<string, string>;
  /**
   * `"host"` ONLY for a mutation whose screen has no code field: a step-up
   * refusal then opens the operator's one prompt and the identical request is
   * re-sent with the first code (ENG-1378). A screen that captures the code
   * itself (useStepUp / StepUpCodeField) sends `headers` and leaves this unset.
   */
  stepUp?: StepUpHandling;
  /** What the operator was doing, for that prompt's copy ("Approve payout"). */
  stepUpAction?: string;
};

export function useApiQuery<TResponse = LegacyApiResponse, TBody = unknown>(
  props: ApiQueryProps<TResponse, TBody>
) {
  const {
    key,
    endPoint,
    method = "GET",
    bodyContent,
    body,
    enabled,
    queryOptions = {},
    dynamicKey,
  } = props;
  const queryKey: QueryKey = dynamicKey
    ? [...key, dynamicKey]
    : [...key, endPoint];
  const requestBody = bodyContent ?? body;

  return useQuery<TResponse, Error, TResponse, QueryKey>({
    queryKey,
    queryFn: () =>
      fetcher<TResponse, TBody>(
        endPoint,
        method,
        method === "POST" ? requestBody : undefined
      ),
    enabled,
    ...DEFAULT_QUERY_OPTIONS,
    ...queryOptions,
  });
}

export function useApiMutation<
  TResponse = LegacyApiResponse,
  TBody = unknown,
>() {
  return useMutation<TResponse, Error, ApiMutationArgs<TBody>>({
    mutationFn: ({
      endPoint,
      method = "POST",
      bodyContent,
      body,
      headers,
      stepUp,
      stepUpAction,
    }) =>
      // The options argument exists only when a caller opted in, so every other
      // mutation reaches `fetcher` with exactly the four arguments it always has.
      stepUp
        ? fetcher<TResponse, TBody>(
            endPoint,
            method,
            bodyContent ?? body,
            headers,
            { stepUp, stepUpAction }
          )
        : fetcher<TResponse, TBody>(
            endPoint,
            method,
            bodyContent ?? body,
            headers
          ),
  });
}

/**
 * `TParam` is what identifies a page: a 1-based page number by default, or
 * whatever a cursor-paged endpoint hands back (see useTicketsInfinite).
 */
type ApiInfiniteQueryProps<TPage, TParam = number> = {
  key: QueryKeyPart[];
  /** Builds the request URL for a given page (a 1-based page number by default). */
  getEndpoint: (pageParam: TParam) => string;
  /** Returns the next page's param, or undefined when there are no more pages. */
  getNextPageParam: (
    lastPage: TPage,
    allPages: TPage[],
    lastPageParam: TParam
  ) => TParam | undefined;
  initialPageParam?: TParam;
  method?: string;
  enabled?: boolean;
  /**
   * Give each page the WHOLE response body instead of `data`.
   *
   * 🔴 SET THIS WHENEVER PAGING DEPENDS ON `pagination`. `fetcher` unwraps a v2
   * body to `data`, so the sibling `pagination` — the total, the page count,
   * the cursor — is discarded before `getNextPageParam` ever sees it. Without
   * it a caller has only the page ITSELF to reason from, and the two things
   * that happens next are both bad:
   *
   *   • paging gets GUESSED from page length ("a full page means there may be
   *     another"), which costs a wasted request whenever the last page is
   *     exactly full, and can never produce a total; or
   *   • the caller hand-rolls a bare `axios` call, which reaches `pagination`
   *     but leans on `axios.defaults` and SKIPS the error-normalising
   *     interceptor, so its failures arrive in a different shape from every
   *     other request in the console.
   *
   * Three services in this repo reached one of those before this existed.
   * `TPage` is then the envelope: `{ data: Row[]; pagination: {...} }`.
   */
  envelope?: boolean;
  /**
   * Per-query overrides for the few options a list actually needs to change —
   * polling, refetch-on-focus and retry. Deliberately NOT the full
   * `UseInfiniteQueryOptions`: `queryKey`, `queryFn` and the paging callbacks
   * are this hook's job, and letting a caller replace them would make two
   * call sites of the same hook behave like different hooks.
   */
  queryOptions?: {
    refetchInterval?: number | false;
    /** Keep polling while the tab is hidden. Off unless a list says otherwise. */
    refetchIntervalInBackground?: boolean;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
    /** Off for a probe whose failure is an answer (e.g. a 400 for an unknown filter). */
    retry?: boolean | number;
  };
};

/**
 * Infinite-scroll variant of useApiQuery. Each page is fetched with the
 * endpoint produced by `getEndpoint(pageParam)` and pages accumulate in
 * `data.pages`. Use `getNextPageParam` to derive the next page from server
 * pagination metadata (e.g. `page < totalPages ? page + 1 : undefined`) — which
 * needs `envelope: true`, or that metadata never arrives.
 */
export function useApiInfiniteQuery<
  TPage = LegacyApiResponse,
  TParam = number,
>({
  key,
  getEndpoint,
  getNextPageParam,
  initialPageParam = 1 as TParam,
  method = "GET",
  enabled,
  envelope = false,
  queryOptions,
}: ApiInfiniteQueryProps<TPage, TParam>) {
  return useInfiniteQuery({
    queryKey: key as QueryKey,
    queryFn: (context) => {
      const url = getEndpoint(context.pageParam as TParam);
      // Both go through axiosInstance and share one error path, so opting in
      // changes what you are given and nothing else.
      return envelope
        ? fetchEnvelope<TPage>(url, method)
        : fetcher<TPage>(url, method);
    },
    initialPageParam,
    getNextPageParam,
    enabled,
    ...DEFAULT_QUERY_OPTIONS,
    // AFTER the defaults, or a caller asking to refetch on focus would be
    // silently overruled by them — the bug this ordering exists to prevent.
    ...queryOptions,
  });
}
